import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '../../../lib/analytics';
import { useMixpanel } from '../../../lib/mixpanel';
import { agentsApi } from '../api/agentsApi';
import type { SendChatMessageInput } from '../types';

export const CHAT_KEYS = {
  all: ['agent-chat'] as const,
  messages: (organizationId: string, agentType: string) =>
    [...CHAT_KEYS.all, 'messages', organizationId, agentType] as const,
};

export function useAgentChatMessages(
  organizationId: string,
  agentType: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: CHAT_KEYS.messages(organizationId, agentType ?? ''),
    queryFn: () => agentsApi.getChatMessages({ organizationId, agentType: agentType! }),
    enabled: enabled && !!organizationId && !!agentType,
  });
}

export function useSendChatMessage() {
  const queryClient = useQueryClient();
  const mp = useMixpanel();

  return useMutation({
    mutationFn: (input: SendChatMessageInput) => agentsApi.sendChatMessage(input),
    onSuccess: (_data, variables) => {
      trackEvent(mp, 'agent.chat_message_sent', {
        agent_type: variables.agentType,
      });
      queryClient.invalidateQueries({
        queryKey: CHAT_KEYS.messages(variables.organizationId, variables.agentType),
      });
    },
  });
}
